import { RRule, rrulestr } from 'rrule';
import moment from 'moment';

export const FUTURE_CAP = 52;
export const FIXED_STEPS_CAP = 5;

const WEEKDAY_CODES = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'];

function stripCount(rrule) {
    if (!rrule) return { count: null, stripped: rrule };
    const match = /(?:^|;)COUNT=(\d+)/i.exec(rrule);
    const count = match ? parseInt(match[1], 10) : null;
    const stripped = rrule
        .replace(/;?COUNT=\d+/i, '')
        .replace(/^;+|;+$/g, '')
        .replace(/;;+/g, ';');
    return { count, stripped };
}

function toTimeKey(value) {
    const t = new Date(value).getTime();
    if (Number.isNaN(t)) return null;
    return Math.floor(t / 1000);
}

export function normalizeExcludedDates(dates = []) {
    const keys = new Set();
    (dates || []).forEach((d) => {
        const key = toTimeKey(d);
        if (key != null) keys.add(key);
    });
    return [...keys]
        .sort((a, b) => a - b)
        .map((key) => new Date(key * 1000).toISOString());
}

export function withoutExcludedDates(dates = [], excludedDates = []) {
    const keys = new Set(
        (excludedDates || []).map(toTimeKey).filter((key) => key != null)
    );
    if (keys.size === 0) return dates;
    return dates.filter((d) => !keys.has(toTimeKey(d)));
}

export { toTimeKey };

export function expandAssessmentReminderRrule({
    rrule,
    dtstart,
    due,
    sentDates = [],
    now = new Date(),
    cap = FUTURE_CAP,
} = {}) {
    if (!rrule || !dtstart || !due) return [];

    const start = new Date(dtstart);
    const dueDate = new Date(due);
    const nowDate = new Date(now);

    if (Number.isNaN(start.getTime()) || Number.isNaN(dueDate.getTime())) {
        return [];
    }
    if (start.getTime() >= dueDate.getTime()) return [];

    const { count: countLimit, stripped } = stripCount(rrule);
    let rule;
    try {
        rule = rrulestr(stripped, { dtstart: start });
    } catch (e) {
        return [];
    }

    const sentKeys = new Set(
        (sentDates || []).map(toTimeKey).filter((k) => k != null)
    );

    const occurrences = rule.between(start, dueDate, true);
    const future = [];

    for (const occ of occurrences) {
        if (occ.getTime() <= nowDate.getTime()) continue;
        if (occ.getTime() >= dueDate.getTime()) continue;
        const key = toTimeKey(occ);
        if (key != null && sentKeys.has(key)) continue;
        future.push(occ);
        if (future.length >= cap) break;
        if (countLimit != null && future.length >= countLimit) break;
    }

    return future;
}

export function buildRruleString({
    freq = 'WEEKLY',
    interval = 1,
    byweekday = [],
    until = null,
    count = null,
} = {}) {
    const options = {
        freq: RRule[freq] != null ? RRule[freq] : RRule.WEEKLY,
        interval: interval && interval > 0 ? interval : 1,
    };
    if (freq === 'WEEKLY' && byweekday && byweekday.length > 0) {
        options.byweekday = byweekday
            .map((code) => RRule[code])
            .filter(Boolean);
    }
    if (count) options.count = count;
    if (until) options.until = new Date(until);

    return new RRule(options)
        .toString()
        .replace(/^DTSTART.*(?:\r?\n)/, '')
        .replace(/^RRULE:/, '');
}

function weekdayToCode(day) {
    if (day == null) return null;
    if (typeof day === 'string') {
        const match = day.match(/[A-Z]{2}$/);
        return match ? match[0] : day;
    }
    if (typeof day === 'number') return WEEKDAY_CODES[day] || null;
    if (typeof day.weekday === 'number') {
        return WEEKDAY_CODES[day.weekday] || null;
    }
    if (typeof day.toString === 'function') {
        const match = String(day.toString()).match(/[A-Z]{2}$/);
        return match ? match[0] : null;
    }
    return null;
}

export function parseRruleToForm(rruleString) {
    if (!rruleString) return null;
    try {
        const rule = rrulestr(rruleString);
        const opts = rule.origOptions || {};
        const freqValue = opts.freq;
        const freqName =
            Object.keys(RRule).find(
                (key) =>
                    ['DAILY', 'WEEKLY', 'MONTHLY'].includes(key) &&
                    RRule[key] === freqValue
            ) || 'WEEKLY';

        let endType = 'never';
        if (opts.count) endType = 'after';
        else if (opts.until) endType = 'on_date';

        const byweekday = []
            .concat(opts.byweekday || [])
            .map(weekdayToCode)
            .filter(Boolean);

        return {
            freq: freqName,
            interval: opts.interval || 1,
            byweekday,
            endType,
            count: opts.count || 10,
            until: opts.until ? moment(opts.until) : null,
        };
    } catch (e) {
        return null;
    }
}

export function looksLikeWeeklySteps(dates) {
    if (!dates || dates.length < 2) return false;
    const sorted = dates
        .map((d) => moment(d))
        .filter((d) => d.isValid())
        .sort((a, b) => a.valueOf() - b.valueOf());
    if (sorted.length < 2) return false;
    for (let i = 1; i < sorted.length; i++) {
        if (sorted[i].diff(sorted[i - 1], 'days') !== 7) return false;
    }
    return true;
}

export function weekdayCodeFromDate(date) {
    const day = moment(date).isoWeekday(); // 1=Mon .. 7=Sun
    return WEEKDAY_CODES[day === 7 ? 6 : day - 1];
}

export function defaultNextOccurrence({
    deliveryDate,
    dueDate,
    futureDates = [],
    sentDates = [],
    now = moment(),
} = {}) {
    let start;
    if (futureDates.length > 0) {
        start = moment(futureDates[0]);
    } else if (deliveryDate) {
        start = moment(deliveryDate)
            .add(7, 'days')
            .hour(9)
            .minute(0)
            .second(0)
            .millisecond(0);
    } else {
        start = now
            .clone()
            .add(7, 'days')
            .hour(9)
            .minute(0)
            .second(0)
            .millisecond(0);
    }

    if (!start.isAfter(now)) {
        start = now.clone().add(1, 'day');
        start.second(0).millisecond(0);
        if (!start.isAfter(now)) start.add(1, 'hour');
    }

    if (sentDates.length > 0) {
        const lastSent = moment.max(sentDates.map((d) => moment(d)));
        if (lastSent.isValid() && !start.isAfter(lastSent)) {
            start = lastSent.clone().add(1, 'day').second(0).millisecond(0);
        }
    }

    if (dueDate && !start.isBefore(moment(dueDate))) {
        start = moment(dueDate).subtract(1, 'day');
    }

    return start;
}
